import { useEffect, useRef, useState } from 'react';
import { FormComponentValueSelectable, prepareFormComponentValues } from 'components/common/form/layout/control';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import { IFilterStatus } from 'components/common/check-box-list/index';

export default function useAsyncData(
    controlName: string,
    tableHeight: number,
    pageSize: number,
    filterStatus: IFilterStatus,
    dataURL: string | false,
) {
    const [actualPage, setActualPage] = useState(0);
    const actualPageRef = useRef(0);
    useEffect(() => {
        actualPageRef.current = actualPage;
    }, [actualPage]);
    const [allDataLoaded, setAllDataLoaded] = useState(false);
    const [fullData, setFullData] = useState<Array<Array<FormComponentValueSelectable>>>([]);

    const [filterTransition, setFilterTransition] = useState(false);
    const [filters, setFilters] = useState(filterStatus);

    const [searchPattern, setSearchPattern] = useState('');
    const [patternTimeout, setPatternTimeout] = useState(0);

    useEffect(() => {
        if (patternTimeout) {
            clearTimeout(patternTimeout);
        }
        setPatternTimeout(
            window.setTimeout(() => {
                setSearchPattern(filterStatus.searchFilterValue);
            }, 300),
        );
    }, [filterStatus.searchFilterValue]);

    useEffect(() => {
        if (!dataURL) {
            return;
        }

        if (JSON.stringify(filters) != JSON.stringify({ ...filterStatus, searchFilterValue: searchPattern })) {
            // Reset data on filters update
            setFilterTransition(true);
            setActualPage(0);
            setAllDataLoaded(false);
            setFullData([]);
            // Set filters
            setFilters({ ...filterStatus, searchFilterValue: searchPattern });
        }
    }, [filterStatus, searchPattern]);

    const filtersString = JSON.stringify(filters);

    useEffect(() => {
        if (!allDataLoaded) {
            refetch();
        }
    }, [actualPage, filtersString, allDataLoaded]);

    const { isLoading, refetch } = useQuery<any | void, Error>({
        queryKey: [controlName, dataURL],
        queryFn: async () => {
            if (!dataURL) {
                return [];
            }

            // Add filter params
            const params: any = {};
            filterStatus.filters.forEach((f) => {
                params[f.name] = f.value;
            });

            const response = await instance.get<APIResponse<Array<any>>>(dataURL, {
                params: { ...params, page: actualPageRef.current, pattern: searchPattern, pageSize: pageSize },
            });
            if (response.data.status == 'OK') {
                const list = fullData.slice();
                let newPage = prepareFormComponentValues(response.data.data) as Array<FormComponentValueSelectable>;
                // const isUncheckedExists = list.find((p) => p.find((r) => !r.selected));
                // if (!isUncheckedExists && list.length) {
                //     // Set all new users as checked
                //     newPage = newPage.map((r) => ({ ...r, selected: true }));
                // }
                list[actualPageRef.current] = newPage;
                if (list[actualPageRef.current].length < pageSize) {
                    setAllDataLoaded(true);
                }
                setFullData(list);
                setFilterTransition(false);
            }
            return [];
        },
    });

    const [resultList, setResultList] = useState<Array<FormComponentValueSelectable>>([]);
    useEffect(() => {
        let list: Array<FormComponentValueSelectable> = [];
        fullData.forEach((l) => (list = list.concat(l)));
        setResultList(list);
    }, [fullData]);

    // For async Data
    const handleScroll = (scrollX: number, scrollY: number) => {
        if (!dataURL || allDataLoaded || isLoading || filterTransition) {
            return;
        }

        const contextHeight = resultList.length * 32;
        const top = Math.abs(scrollY);

        if (contextHeight > 0 && contextHeight - top - tableHeight < 500) {
            // Trigger Load next page
            setActualPage(actualPage + 1);
        }
    };

    return { resultList, isLoading: isLoading || filterTransition, handleScroll };
}
